import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { usePageChat } from '@/hooks';
import { ChatInput } from '../components/chat/ChatInput';
import { QuickSuggestionsBlock } from '../components/chat/QuickSuggestionsBlock';
import { Header } from '@components/layout';
import { Footer } from '@components/layout';
import { ChatContainer } from '../components/chat/ChatContainer';
import { LoadingState } from '../components/common/LoadingState';
import { SchemaMarkup } from '@/components/common/SchemaMarkup';
import { getResearchBySlug } from '@/config/research.config';

interface ResearchPageProps {
    showBanner: () => void;
}

export const ResearchPage: React.FC<ResearchPageProps> = ({ showBanner }) => {
    const { slug } = useParams<{ slug: string }>();
    const { messages, isLoading, streamingMessage, sendMessage, assistant, loadingAssistants, messagesEndRef, currentTool } = usePageChat(slug);
    const research = slug ? getResearchBySlug(slug) : undefined;

    if (loadingAssistants || !assistant) {
        return (
            <>
                <Header />
                <div className="pt-20 sm:pt-24 md:pt-28 pb-28 px-2 sm:px-4">
                    <div className="max-w-3xl mx-auto"><LoadingState message="Loading..." /></div>
                </div>
                <Footer />
            </>
        );
    }

    if (!research) {
        return (
            <>
                <Header />
                <div className="pt-20 sm:pt-24 md:pt-28 pb-28 px-2 sm:px-4">
                    <div className="max-w-3xl mx-auto">
                        <h1 className="text-2xl font-bold text-white mb-4">Research guide not found</h1>
                        <Link to="/research" className="text-cyan-400 hover:text-cyan-300 underline">
                            View all research guides
                        </Link>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    const [h1Main, h1Sub] = research.h1.includes(' — ')
        ? research.h1.split(' — ')
        : [research.h1, ''];

    const faqSchema = {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: research.faqs.map(item => ({
            '@type': 'Question',
            name: item.question,
            acceptedAnswer: { '@type': 'Answer', text: item.answer },
        })),
    };

    return (
        <>
            <SchemaMarkup schema={faqSchema} />
            <Header />
            <div className="pt-20 sm:pt-24 md:pt-28 pb-28 sm:pb-32 md:pb-36 px-2 sm:px-3 md:px-4">
                <div className="max-w-3xl mx-auto">
                    <div className="min-h-[60vh] space-y-2 sm:space-y-3 md:space-y-4 py-2 sm:py-3 md:py-4">
                        <article className="mb-6">

                            {/* H1 */}
                            <h1 className="text-lg sm:text-2xl md:text-3xl font-bold text-white mb-2">
                                {h1Main}
                                {h1Sub && (
                                    <span className="block text-sm sm:text-base md:text-lg font-medium text-gray-400 mt-1">
                                        {h1Sub}
                                    </span>
                                )}
                            </h1>

                            {/* Intro */}
                            <div className="mb-4">
                                <p className="text-sm sm:text-base text-gray-300 mb-3">{research.intro}</p>
                                <p className="text-sm sm:text-base text-gray-300">{research.whatMakesGood}</p>
                            </div>

                            {/* Comparison Table */}
                            {research.tableRows.length > 0 && (
                                <div className="mb-6">
                                    <h2 className="text-base sm:text-xl font-bold text-white mt-4 sm:mt-6 mb-2 sm:mb-3">
                                        {h1Main} — At a Glance
                                    </h2>
                                    <div className="overflow-x-auto rounded-xl border border-gray-600">
                                        <table className="w-full text-sm text-gray-300">
                                            <thead>
                                                <tr className="bg-gray-800">
                                                    {research.tableHeaders.map((header, i) => (
                                                        <th key={i} className="text-left px-3 py-2 text-white font-semibold whitespace-nowrap">
                                                            {header}
                                                        </th>
                                                    ))}
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-700">
                                                {research.tableRows.map((row, i) => (
                                                    <tr key={i} className={i % 2 === 0 ? 'bg-[#1a1a1a]' : 'bg-gray-900'}>
                                                        <td className="px-3 py-2 font-medium text-white whitespace-nowrap">{row.vehicle}</td>
                                                        {row.values.map((val, j) => (
                                                            <td key={j} className="px-3 py-2 whitespace-nowrap">{val}</td>
                                                        ))}
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}

                            {/* Vehicle Sections */}
                            {research.vehicleSections.map((section, i) => (
                                <div key={i} className="mb-4">
                                    <h2 className="text-base sm:text-xl font-bold text-white mt-4 sm:mt-6 mb-2 sm:mb-3">
                                        <Link to={section.path} className="text-cyan-400 hover:text-cyan-300 transition-colors">
                                            {section.vehicle}
                                        </Link>
                                    </h2>
                                    <ul className="list-disc list-inside space-y-1 text-sm sm:text-base text-gray-300 ml-2 mb-3">
                                        {section.bullets.map((bullet, j) => (
                                            <li key={j}>{bullet}</li>
                                        ))}
                                    </ul>
                                    {section.comparisons.length > 0 && (
                                        <p className="text-sm text-gray-400">
                                            Compare:{' '}
                                            {section.comparisons.map((c, j) => (
                                                <span key={j}>
                                                    <Link to={c.path} className="text-cyan-400 hover:text-cyan-300 underline transition-colors">
                                                        {c.label}
                                                    </Link>
                                                    {j < section.comparisons.length - 1 ? ', ' : ''}
                                                </span>
                                            ))}
                                        </p>
                                    )}
                                </div>
                            ))}

                            {/* How to Choose */}
                            <div className="mb-4">
                                <h2 className="text-base sm:text-xl font-bold text-white mt-4 sm:mt-6 mb-2 sm:mb-3">
                                    How to Choose
                                </h2>
                                <ul className="list-disc list-inside space-y-1 text-sm sm:text-base text-gray-300 ml-2">
                                    {research.howToChoose.map((item, i) => (
                                        <li key={i}>{item}</li>
                                    ))}
                                </ul>
                            </div>

                            {/* Comparisons */}
                            {research.comparisons.length > 0 && (
                                <div className="mb-4">
                                    <h2 className="text-base sm:text-xl font-bold text-white mt-4 sm:mt-6 mb-2 sm:mb-3">
                                        Related Vehicle Comparisons
                                    </h2>
                                    <ul className="list-disc list-inside space-y-1.5 text-sm sm:text-base text-gray-300 ml-2">
                                        {research.comparisons.map((c, i) => (
                                            <li key={i}>
                                                <Link to={c.path} className="text-cyan-400 hover:text-cyan-300 underline transition-colors">
                                                    {c.label}
                                                </Link>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            {/* FAQ */}
                            <div className="mb-4">
                                <h2 className="text-base sm:text-xl font-bold text-white mt-4 sm:mt-6 mb-2 sm:mb-3">
                                    Frequently Asked Questions
                                </h2>
                                <div className="space-y-4">
                                    {research.faqs.map((item, i) => (
                                        <div key={i}>
                                            <h3 className="text-sm sm:text-base font-semibold text-gray-200 mb-1">{item.question}</h3>
                                            <p className="text-sm sm:text-base text-gray-300">{item.answer}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Dealerships */}
                            {research.dealerships.length > 0 && (
                                <div className="mb-4">
                                    <h2 className="text-base sm:text-xl font-bold text-white mt-4 sm:mt-6 mb-2 sm:mb-3">
                                        Find Vehicles Near You
                                    </h2>
                                    <ul className="list-disc list-inside space-y-1.5 text-sm sm:text-base text-gray-300 ml-2">
                                        {research.dealerships.map((d, i) => (
                                            <li key={i}>
                                                <Link
                                                    to={`/dealerships/${d.slug}`}
                                                    className="text-cyan-400 hover:text-cyan-300 underline transition-colors"
                                                >
                                                    {d.name}
                                                </Link>
                                                {' '}— {d.city}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            {/* Related Research */}
                            {research.relatedResearch.length > 0 && (
                                <div className="mb-4">
                                    <h2 className="text-base sm:text-xl font-bold text-white mt-4 sm:mt-6 mb-2 sm:mb-3">
                                        More Vehicle Research
                                    </h2>
                                    <ul className="list-disc list-inside space-y-1.5 text-sm sm:text-base text-gray-300 ml-2">
                                        {research.relatedResearch.map((r, i) => (
                                            <li key={i}>
                                                <Link to={r.path} className="text-cyan-400 hover:text-cyan-300 underline transition-colors">
                                                    {r.name}
                                                </Link>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            {/* Chat CTA */}
                            <QuickSuggestionsBlock
                                topLine="Ready to find your vehicle?"
                                title="Find Vehicles Near You"
                                suggestions={research.quickSuggestions}
                                onSend={sendMessage}
                            />

                        </article>

                        {messages.length > 0 && (
                            <ChatContainer
                                messages={messages}
                                assistant={assistant}
                                isLoading={isLoading}
                                streamingMessage={streamingMessage}
                                currentTool={currentTool}
                            />
                        )}
                        <div ref={messagesEndRef} />
                    </div>
                </div>
            </div>
            <ChatInput
                assistant={assistant}
                onSendMessage={sendMessage}
                disabled={isLoading}
                showBanner={showBanner}
            />
        </>
    );
};
